'use client';

import {
  Avatar as MBAvatar,
  Checkbox as MBCheckbox,
} from '@mokoboko/components';
import type { ReactNode } from 'react';

import { InstructorPicker } from '@/components/instructor-picker';
import { getAbbreviatedName } from '@/lib/utils';
import type { Instructor } from '@/state/class-availabilities';

/** Capitalize first letter of each word only if the word is fully lowercase. */
const formatName = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((w) => (w === w.toLowerCase() ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');

const PICKER_FROM = 5;

// No ids is All, every pill ticked. The last ticked pill stays ticked: with
// none, no class time would show.
const toggledPillIds = (
  instructors: Instructor[],
  selectedIds: string[],
  id: string
): string[] | null => {
  const ticked =
    selectedIds.length === 0
      ? instructors.map((i) => i.admin_user_id)
      : selectedIds;
  if (!ticked.includes(id)) return [...ticked, id];
  const next = ticked.filter((t) => t !== id);
  return instructors.some((i) => next.includes(i.admin_user_id)) ? next : null;
};

// The one button fits beside the heading; pills that wrap go under it. On a
// phone the button spans the card, so it drops under the heading too.
export const InstructorFilter = ({
  heading,
  instructors,
  selectedIds,
  onChange,
}: {
  heading: ReactNode;
  instructors: Instructor[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) => {
  if (instructors.length >= PICKER_FROM) {
    return (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {heading}
        <InstructorPicker
          instructors={instructors}
          selectedIds={selectedIds}
          onChange={onChange}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {heading}
      <div className="flex flex-wrap items-center gap-3">
        {instructors.map((item) => (
          <div
            key={item.admin_user_id}
            className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-white py-2 pl-3 pr-2 text-sm font-semibold shadow-skeu-secondary outline-none transition-all duration-200 hover:bg-gray-200 active:translate-y-px active:scale-[0.975]"
            onClick={() => {
              const next = toggledPillIds(
                instructors,
                selectedIds,
                item.admin_user_id
              );
              if (next) onChange(next);
            }}
          >
            <MBCheckbox
              checked={
                selectedIds.length === 0 ||
                selectedIds.includes(item.admin_user_id)
              }
              aria-label={getAbbreviatedName(item.first_name, item.last_name)}
            />
            <span className="text-sm font-medium leading-5 text-gray-900">
              {getAbbreviatedName(item.first_name, item.last_name)}
            </span>
            <MBAvatar
              size="xs"
              name={formatName(`${item.first_name} ${item.last_name ?? ''}`)}
              src={item?.profile_picture ?? undefined}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
